import {
  Button,
  DataTable,
  FormGroup,
  Input,
  Label,
  Select,
} from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useRedis } from '@/hooks/useRedis'
import { NoDataComponent } from '@/pages/bizMgmt/bizMgmt'
import { tableStyles } from '@/styles/dataTable/style'
import commonUtils from '@/utils/commonUtils'
import { useEffect, useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export interface DataRow {
  rnum: string
  cmpnyCd: string
  cstId: string
  joinDivCd: string
  snsId: string
  userId: string
  password: string
  ci: string
  name: string
  nickName: string
  gender: string
  birth: string
  email: string
  smsOptIn: string
  jobCd: string
  homePostCd: string
  homeAddr: string
  homeDaddr: string
  homeArCd: string
  homeExcNo: string
  homeSubNo: string
  mnc: string
  mblNetCd: string
  mblExcNo: string
  mblSubNo: string
  wrcPostCd: string
  wrcAddr: string
  wrcDaddr: string
  wrcArCd: string
  wrcExcNo: string
  wrcSubNo: string
  wrcNm: string
  department: string
  jbps: string
  lastLogin: string
  accessIp: string
  pwErrCount: string
  state: string
  stateNm: string
  withDrawDttm: string
  refreshToken: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

interface GetSrchData {
  srchOpt?: string //검색옵션
  srchStr?: string //검색어
  state?: string
  gender?: string
}

const initialSrchData = {
  srchOpt: '01',
  srchStr: '',
  state: '',
  gender: '',
}

const MemberMgmt: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const axios = useAxios()

  const [data, setData] = useState<DataRow[]>([])

  const [srchData, setSrchData] = useState(initialSrchData)

  const { getComCodes } = useRedis()

  useEffect(() => {
    const prevState = location.state?.prevSearchState
    if (prevState) {
      setSrchData(prevState)
      setData(prevState.results)
    } else {
      getMemberList()
    }
  }, [location])

  // 회원상세 이동
  const handleNavigateDetail = (
    cstId: string,
    userId: string,
    type: 'view' | 'update',
  ) => {
    navigate('/memberMgmt/detail', {
      state: {
        cstId: cstId,
        userId: userId,
        type: type,
        prevSearchState: { ...srchData, results: data },
      },
    })
  }

  //약관버튼 클릭시 약관동의조회 화면으로 이동
  const handleButtonTermsClick = (cstId: string, userId: string) => {
    navigate('/memberMgmt/termsAgree', {
      state: {
        cstId: cstId,
        userId: userId,
        memberSearchState: { ...srchData, results: data },
      },
    })
  }

  //로그인이력 클릭시 로그인이력화면으로 이동
  const handleButtonHistClick = (cstId: string, userId: string) => {
    navigate('/loginHistMgmt', {
      state: {
        cstId,
        userId,
        memberSearchState: { ...srchData, results: data },
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getMemberList()
    }
  }

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement // 타입 캐스팅
    setSrchData((prev) => ({ ...prev, [name]: value }))
  }

  const getMemberList = async () => {
    try {
      const searchParams: GetSrchData = {
        srchOpt: srchData.srchOpt,
        srchStr: srchData.srchStr,
        state: srchData.state,
        gender: srchData.gender,
      }

      const response = await axios.get('/member/list', {
        params: searchParams,
      })

      const listData = response.data

      if (Array.isArray(listData)) {
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching member list:', error)
    }
  }

  const genderList = [
    { code: '', codeNm: '전체' },
    { code: 'M', codeNm: '남성' },
    { code: 'F', codeNm: '여성' },
  ]

  const columns: TableColumn<DataRow>[] = [
    {
      name: 'No.',
      selector: (row) => parseInt(row.rnum, 10),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.rnum}</div>
      ),
    },
    {
      name: '회원번호',
      selector: (row) => row.cstId,
      sortable: true,
      wrap: true,
      center: true,
    },
    {
      name: 'ID',
      selector: (row) => row.userId,
      sortable: true,
      wrap: true,
    },
    {
      name: '이름',
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
    },
    {
      name: '닉네임',
      selector: (row) => row.nickName,
      sortable: true,
      wrap: true,
    },
    {
      name: '최종로그인일시',
      selector: (row) => commonUtils.formatDate(row.lastLogin, true),
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '상태',
      selector: (row) => row.stateNm,
      sortable: true,
      wrap: true,
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      wrap: true,
    },
    {
      name: '수정일시',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate),
      sortable: true,
      wrap: true,
    },
    {
      name: '',
      cell: (row) => (
        <FlexWrapper gap="20px">
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonHistClick(row.cstId, row.userId)}
          >
            로그인이력
          </Button>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonTermsClick(row.cstId, row.userId)}
          >
            약관
          </Button>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() =>
              handleNavigateDetail(row.cstId, row.userId, 'update')
            }
          >
            수정
          </Button>
        </FlexWrapper>
      ),
      minWidth: '300px',
    },
  ]

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>회원관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <FormGroup>
              <Label htmlFor={''}>상태</Label>
              <Select
                id="state"
                name="state"
                width="150px"
                onChange={handleSearch}
                value={srchData.state}
              >
                <option value="">전체</option>
                {getComCodes('MEM_STATE').map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>성별</Label>
              <Select
                id="gender"
                name="gender"
                width="100px"
                onChange={handleSearch}
                value={srchData.gender}
              >
                {genderList.map(({ code, codeNm }) => (
                  <option key={code} value={code}>
                    {codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'srchOpt'}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Select
                  id="srchOpt"
                  name="srchOpt"
                  width="100px"
                  value={srchData.srchOpt}
                  onChange={handleSearch}
                >
                  <option value="01">이름</option>
                  <option value="02">ID</option>
                  <option value="03">닉네임</option>
                  <option value="04">이메일</option>
                </Select>
                <Input
                  placeholder="검색어"
                  width="300px"
                  id="srchStr"
                  name="srchStr"
                  value={srchData.srchStr}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
                <Button onClick={getMemberList} color="neverbe">
                  검색
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>
      <MemberSearchContainerWrapper>
        <DataTable
          columns={columns}
          data={data}
          customStyles={tableStyles}
          pagination={true}
          paginationPerPage={50}
          paginationRowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
          defaultSortAsc={false}
          noDataComponent={<NoDataComponent />}
        />
      </MemberSearchContainerWrapper>
    </>
  )
}

const MemberSearchContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 700px;

  .select-red {
    color: red;
  }
  .select-blue {
    color: blue;
  }
  .select-default {
    color: inherit;
  }
  .option-blue {
    color: blue;
  }
  .option-red {
    color: red;
  }
`
export default MemberMgmt
